<script lang="ts">
	import { onMount, onDestroy, createEventDispatcher } from 'svelte';
	import { writable, get } from 'svelte/store';
	import {
		SvelteFlow,
		Controls,
		Background,
		MiniMap,
		type Node,
		type Edge,
		type Connection,
		MarkerType,
	} from '@xyflow/svelte';
	import type { FinalConnectionState } from '@xyflow/system';
	import '@xyflow/svelte/dist/style.css';

	import type { ISceneObj } from '../../../types/SceneType';
	import { EDGE_COLORS, type FlowNode, type FlowEdge } from '../types/flow-types';
	import {
		flowEditorState,
		edgeActionBus,
		layoutTrigger,
		generateUniqueNodeId,
		registerFlowCanvasPositionFlush,
		registerFlowCanvasSync,
		flushLiveFlowCanvasSync,
		flowCanvasTheme,
	} from '../../../stores/flow_editor';
	import { buildDefaultNodeData } from '../core';
	import { getNodeTypeConfigByPaletteKey } from '../core/config/node-types';
	import { getFlowTemplate } from '../core/templates/flow-templates';
	import { sceneToGraph } from '../converters/SceneToFlow';

	import TriggerNode from '../nodes/TriggerNode.svelte';
	import LogicGateNode from '../nodes/LogicGateNode.svelte';
	import ActionNode from '../nodes/ActionNode.svelte';
	import NotificationNode from '../nodes/NotificationNode.svelte';
	import GroupActionNode from '../nodes/GroupActionNode.svelte';
	import SceneActionNode from '../nodes/SceneActionNode.svelte';
	import ScriptActionNode from '../nodes/ScriptActionNode.svelte';
	import TimerNode from '../nodes/TimerNode.svelte';
	import SetVariableNode from '../nodes/SetVariableNode.svelte';
	import SwitchNode from '../nodes/SwitchNode.svelte';
	import NoteNode from '../nodes/NoteNode.svelte';
	import HysteresisConditionNode from '../nodes/HysteresisConditionNode.svelte';
	import ActionSequenceNode from '../nodes/ActionSequenceNode.svelte';
	import VariableSwitchNode from '../nodes/VariableSwitchNode.svelte';
	import PresenceConditionNode from '../nodes/conditions/PresenceConditionNode.svelte';
	import VirtualComponentConditionNode from '../nodes/conditions/VirtualComponentConditionNode.svelte';
	import VirtualComponentActionNode from '../nodes/VirtualComponentActionNode.svelte';
	import QuickNodesMenu from './QuickNodesMenu.svelte';
	import FlowPaneContextMenu from './FlowPaneContextMenu.svelte';
	import FlowNodeContextMenu from './FlowNodeContextMenu.svelte';
	import CanvasContextBridge from './FlowCanvasContextBridge.svelte';
	import MidpointEdge from './MidpointEdge.svelte';
	import { applyBranchAwareLayout } from './branchAwareLayout';
	import { buildConditionNodeTypes } from '../nodes/conditions/registry';
	import { assignParallelEdgePathOffsetsForRender } from '../helpers/parallelEdgePathOffsets';
	import { collectProblematicNodes } from '../helpers/nodeValidation';
	import { readFlowSelectionClipboard } from '../helpers/flowSelectionClipboard';
	import { trackFlowUxEvent } from '../core/ux/flow-ux-telemetry';
	import { deriveFlowIslands } from '../helpers/flowIslands';
	import { createReusableSubflow, saveReusableSubflow } from '../core/templates/reusable-subflows';

	export let scene: ISceneObj | null;
	export let isMobile: boolean = false;

	const dispatch = createEventDispatcher();
	type LoadStatusSource = 'saved' | 'reconstructed' | 'example';

	// Local writable stores required by SvelteFlow - mirrors of flowEditorState
	const nodes = writable<Node[]>([]);
	const edges = writable<Edge[]>([]);

	// Build node types: condition types from registry + other node types
	const nodeTypes = {
		...buildConditionNodeTypes(),
		trigger: TriggerNode,
		logicGate: LogicGateNode,
		action: ActionNode,
		notificationAction: NotificationNode,
		groupAction: GroupActionNode,
		sceneAction: SceneActionNode,
		alarmAction: SceneActionNode,
		scriptAction: ScriptActionNode,
		timer: TimerNode,
		setVariable: SetVariableNode,
		switchNode: SwitchNode,
		note: NoteNode,
		hysteresisCondition: HysteresisConditionNode,
		actionSequence: ActionSequenceNode,
		variableSwitch: VariableSwitchNode,
		presenceCondition: PresenceConditionNode,
		virtualComponentEvent: VirtualComponentConditionNode,
		virtualComponentCondition: VirtualComponentConditionNode,
		virtualComponentAction: VirtualComponentActionNode,
	};

	// Register custom edge types
	const edgeTypes = {
		midpoint: MidpointEdge,
	} as any;

	const nodeTypesMap = nodeTypes as any;

	// Background variant cast
	const bgVariant = 'dots' as any;

	const READABLE_FIT_MIN_ZOOM = 0.65;
	let flowInstance: any;
	let initialized = false;

	/** Select a node and pan/zoom the canvas to it (used by load-warning chips in FlowEditor). */
	export function focusNode(nodeId: string) {
		if (!nodeId) return;
		flowEditorState.selectNode(nodeId);
		queueMicrotask(() => {
			flowInstance?.fitView?.({
				nodes: [{ id: nodeId }],
				maxZoom: 1,
				padding: 0.45,
				duration: 280,
			});
		});
	}
	let lastSceneId: string = '';
	let lastSceneContentKey: string = '';
	let isDragging = false;
	/** True while user drags from a handle to create a connection — suppresses card selection sync. */
	let isConnecting = false;
	let selectionBeforeConnect: string[] = [];
	let suppressNodeClickUntil = 0;
	let suppressPaneClickUntil = 0;
	let pendingPlacement: {
		type?: string;
		paletteKey?: string;
		templateId?: string;
		initialData?: Record<string, unknown>;
		label: string;
		icon: string;
		color: string;
	} | null = null;
	let placementPointer = { x: 0, y: 0 };
	let placementPointerVisible = false;
	let semanticZoom: 'detail' | 'compact' | 'overview' = 'detail';
	let collapsedIslandIds = new Set<string>();
	let showIslandManager = false;
	$: islandGroups = deriveFlowIslands($flowEditorState.nodes, $flowEditorState.edges);
	$: collapsedNodeIds = new Set(
		islandGroups
			.filter((island) => collapsedIslandIds.has(island.id))
			.flatMap((island) => island.nodeIds),
	);

	function toggleIsland(islandId: string) {
		const next = new Set(collapsedIslandIds);
		next.has(islandId) ? next.delete(islandId) : next.add(islandId);
		collapsedIslandIds = next;
	}

	function renameIsland(rootNodeId: string, event: Event) {
		const name = (event.currentTarget as HTMLInputElement).value.trim();
		const root = get(flowEditorState).nodes.find((node) => node.id === rootNodeId);
		if (!root) return;
		flowEditorState.updateNode(rootNodeId, { data: { ...root.data, islandName: name } });
	}

	/** Read XY Flow's viewport scale without coupling card components to the canvas library. */
	function observeSemanticZoom(canvas: HTMLElement) {
		let frame = 0;
		const measure = () => {
			const viewport = canvas.querySelector('.svelte-flow__viewport') as HTMLElement | null;
			const transform = viewport?.style.transform ?? '';
			const match = transform.match(/scale\(([-\d.]+)\)/);
			const scale = Number(match?.[1] ?? 1);
			semanticZoom = scale < 0.42 ? 'overview' : scale < 0.68 ? 'compact' : 'detail';
		};
		const schedule = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(measure);
		};
		const observer = new MutationObserver(schedule);
		observer.observe(canvas, { subtree: true, attributes: true, attributeFilter: ['style'] });
		schedule();
		return {
			destroy: () => {
				cancelAnimationFrame(frame);
				observer.disconnect();
			},
		};
	}

	// Pane right-click menu (Add node / Paste)
	let paneMenuVisible = false;
	let paneMenuX = 0;
	let paneMenuY = 0;
	let addAtFlowX = 0;
	let addAtFlowY = 0;

	// Quick nodes panel (search + list) — used for pane add, connect-end drop, etc.
	let quickNodesVisible = false;
	let quickNodesScreenX = 0;
	let quickNodesScreenY = 0;
	let quickFlowX = 0;
	let quickFlowY = 0;
	let quickPlacementPurpose: 'free' | 'connect' = 'free';
	let quickTitle = 'Add node';
	let quickHint = '';

	let pendingConnectFrom: { source: string; sourceHandle: string | null } | null = null;
	const PROXIMITY_EDGE_ID = '__proximity-preview__';
	const PROXIMITY_DISTANCE = 170;
	let proximityConnection: Connection | null = null;
	let connectStartPoint: { x: number; y: number } | null = null;
	let armedConnection: { source: string; sourceHandle: string | null; x: number; y: number } | null =
		null;
	let armedPointer = { x: 0, y: 0 };
	let contextualPlacement: {
		x: number;
		y: number;
		source?: string;
		sourceHandle?: string | null;
		edgeId?: string;
	} | null = null;

	// Node right-click menu
	let nodeCtxVisible = false;
	let nodeCtxX = 0;
	let nodeCtxY = 0;
	let nodeCtxTargetId: string | null = null;

	$: canFlowPaste =
		($flowEditorState.clipboard?.nodes.length ?? 0) > 0 ||
		(readFlowSelectionClipboard()?.nodes.length ?? 0) > 0;

	// screenToFlowPosition from useSvelteFlow (set by FlowCanvasContextBridge inside SvelteFlow)
	const screenToFlowPositionFn = writable<
		((pos: { x: number; y: number }) => { x: number; y: number }) | null
	>(null);

	// Double-tap detection for mobile context menu
	let lastTapTime = 0;

	function sameIdSet(a: string[], b: string[]) {
		if (a.length !== b.length) return false;
		const s = new Set(a);
		return b.every((id) => s.has(id));
	}

	/** Per-type drag surface — must match a real element inside each node Svelte component. */
	const DRAG_HANDLE_BY_NODE_TYPE: Record<string, string> = {
		note: '.note-header',
	};

	function dragHandleSelectorForNode(node: Node): string {
		return DRAG_HANDLE_BY_NODE_TYPE[String(node.type)] ?? '.flow-node-card-inner';
	}

	/** Clicks vs drags: movement beyond this distance starts a drag instead of opening the inspector. */
	const NODE_CLICK_DISTANCE = 6;

	function endConnectionDrag(restoreSelection = true) {
		isConnecting = false;
		if (restoreSelection && selectionBeforeConnect.length > 0) {
			flowEditorState.setSelectedNodeIds([...selectionBeforeConnect]);
		}
		selectionBeforeConnect = [];
	}

	export function beginNodePlacement(
		type: string,
		paletteKey: string,
		initialData?: Record<string, unknown>,
	) {
		const config = getNodeTypeConfigByPaletteKey(paletteKey);
		const defaults = buildDefaultNodeData(type, paletteKey);
		pendingPlacement = {
			type,
			paletteKey,
			initialData: initialData ? structuredClone(initialData) : undefined,
			label: String(config?.label ?? defaults.label ?? type),
			icon: config?.icon ?? 'fa-plus',
			color: config?.color ?? '#3b82f6',
		};
		placementPointerVisible = false;
		paneMenuVisible = false;
		quickNodesVisible = false;
		nodeCtxVisible = false;
		flowEditorState.setEditorOpen(false);
		trackFlowUxEvent({
			name: 'placement_started',
			surface: 'canvas',
			paletteKey,
			kind: 'node',
		});
	}

	export function addContextualNode(
		type: string,
		paletteKey: string,
		initialData?: Record<string, unknown>,
	): boolean {
		const context = contextualPlacement;
		contextualPlacement = null;
		if (!context) return false;
		if (context.edgeId) {
			handleEdgeMidpointInsert(
				context.edgeId,
				context.x,
				context.y,
				type,
				paletteKey,
				initialData,
			);
			return true;
		}
		const node: FlowNode = {
			id: generateUniqueNodeId(),
			type: type as any,
			position: { x: context.x - 100, y: context.y - 36 },
			data: {
				...buildDefaultNodeData(type, paletteKey),
				...(initialData ? structuredClone(initialData) : {}),
			} as any,
			validated: true,
			errors: [],
		};
		flowEditorState.addNode(node);
		if (context.source) {
			handleConnect({
				source: context.source,
				target: node.id,
				sourceHandle: context.sourceHandle ?? null,
				targetHandle: 'input',
			});
		}
		flushLiveFlowCanvasSync();
		flowEditorState.setEditorOpen(false);
		return true;
	}

	export function beginTemplatePlacement(templateId: string) {
		const template = getFlowTemplate(templateId);
		if (!template) return;
		pendingPlacement = {
			templateId,
			label: `${template.name} · ${template.nodes.length} nodes`,
			icon: template.icon,
			color: template.color,
		};
		placementPointerVisible = false;
		paneMenuVisible = false;
		quickNodesVisible = false;
		nodeCtxVisible = false;
		flowEditorState.setEditorOpen(false);
		trackFlowUxEvent({
			name: 'placement_started',
			surface: 'canvas',
			paletteKey: templateId,
			kind: 'template',
		});
	}

	function cancelNodePlacement() {
		if (pendingPlacement) {
			trackFlowUxEvent({
				name: 'placement_cancelled',
				surface: 'canvas',
				paletteKey: pendingPlacement.templateId ?? pendingPlacement.paletteKey ?? 'unknown',
				kind: pendingPlacement.templateId ? 'template' : 'node',
			});
		}
		pendingPlacement = null;
		placementPointerVisible = false;
	}

	function handlePlacementPointerMove(event: PointerEvent) {
		const target = event.currentTarget as HTMLElement;
		const rect = target.getBoundingClientRect();
		if (armedConnection)
			armedPointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
		if (!pendingPlacement || event.pointerType === 'touch') return;
		placementPointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
		placementPointerVisible = true;
	}

	function handleConnectStart(
		event: MouseEvent | TouchEvent,
		_params: { nodeId: string | null; handleId: string | null; handleType: string | null },
	) {
		connectStartPoint = connectEndClientPos(event);
		suppressNodeClickUntil = Date.now() + 500;
		selectionBeforeConnect = [...get(flowEditorState).selectedNodeIds];
		isConnecting = true;
		flowEditorState.setSelectedNodeIds([]);
		nodes.update((nds) => nds.map((n) => ({ ...n, selected: false })));
	}

	/** Push flowEditorState nodes/edges into the local XYFlow writables (SvelteFlow source). */
	function syncStoreToCanvasWritables() {
		const st = get(flowEditorState);
		const sel = new Set(st.selectedNodeIds);
		const sim = st.simulationHighlight;
		const simN = sim ? new Set(sim.nodeIds) : null;
		const simE = sim ? new Set(sim.edgeIds) : null;
		const baseNodes = st.nodes as Node[];
		nodes.set(
			baseNodes.map((n) => {
				let cls = String((n as any).class || '');
				if (simN?.has(n.id)) cls = cls ? `${cls} flow-sim-node` : 'flow-sim-node';
				return {
					...n,
					hidden: collapsedNodeIds.has(n.id),
					selected: sel.has(n.id),
					dragHandle: dragHandleSelectorForNode(n),
					class: cls.trim() || undefined,
				} as Node;
			}),
		);
		const baseEdges = assignParallelEdgePathOffsetsForRender(st.edges as Edge[]) as Edge[];
		edges.set(
			applyEdgeStyles(baseEdges).map((e) => {
				let cls = String((e as any).class || '');
				if (simE?.has(e.id)) cls = cls ? `${cls} flow-sim-edge` : 'flow-sim-edge';
				return {
					...e,
					hidden:
						collapsedNodeIds.has(String(e.source)) || collapsedNodeIds.has(String(e.target)),
					class: cls.trim() || undefined,
				} as Edge;
			}),
		);
	}

	// Sync flowEditorState → local SvelteFlow stores, but skip during drag.
	// During drag, @xyflow/svelte owns node positions. Calling nodes.set() while
	// a drag is in flight resets positions and corrupts the drag state.
	$: if (initialized && !isDragging && !isConnecting) {
		collapsedNodeIds;
		syncStoreToCanvasWritables();
	}

	onMount(() => {
		registerFlowCanvasPositionFlush(flushLiveNodePositionsToStore);
		registerFlowCanvasSync(syncStoreToCanvasWritables);

		// Scene loading is handled by reactive block (watches sceneId)
		// Listen for property changes from property panel
		const unsubscribeEditor = flowEditorState.subscribe((state) => {
			if (state.selectedNodeId && state.propertyChanges) {
				handlePropertyChange(state.selectedNodeId, state.propertyChanges);
			}
			// Inspector updates replace node data in flowEditorState. Keep XYFlow's local
			// writables in step so card summaries and validation change immediately.
			if (initialized && !isDragging && !isConnecting) {
				syncStoreToCanvasWritables();
			}
		});

		// Listen to edge midpoint action bus
		const unsubscribeEdgeBus = edgeActionBus.subscribe((action) => {
			if (!action) return;
			if (action.kind === 'delete') {
				flowEditorState.removeEdge(action.edgeId);
			} else if (action.kind === 'requestInsert') {
				contextualPlacement = { edgeId: action.edgeId, x: action.x, y: action.y };
				dispatch('requestadd', { entryPoint: 'connection' });
			} else if (action.kind === 'reconnectTarget') {
				flowEditorState.update((state) => ({
					...state,
					edges: state.edges.map((edge) =>
						edge.id === action.edgeId
							? { ...edge, target: action.target, targetHandle: action.targetHandle }
							: edge,
					),
					isDirty: true,
				}));
			}
			edgeActionBus.set(null);
		});

		// Listen to layout trigger (toolbar Auto Layout button)
		const unsubscribeLayout = layoutTrigger.subscribe((trigger) => {
			if (trigger <= 0) return;
			const state = get(flowEditorState);
			if (state.nodes.length === 0) return;
			const layouted = applyBranchAwareLayout(state.nodes as Node[], state.edges as Edge[]);
			const styledEdges = applyEdgeStyles(layouted.edges);
			flowEditorState.setNodesAndEdges(layouted.nodes as FlowNode[], styledEdges as FlowEdge[], {
				markDirty: true,
			});
		});

		function isTypingTarget(target: EventTarget | null): boolean {
			if (!target || !(target instanceof HTMLElement)) return false;
			if (target.isContentEditable) return true;
			const tag = target.tagName?.toLowerCase();
			if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
			if (target.closest('[contenteditable="true"]')) return true;
			if (target.getAttribute('role') === 'textbox') return true;
			return false;
		}

		function handleKeyDown(e: KeyboardEvent) {
			if (e.defaultPrevented) return;
			if (isTypingTarget(e.target)) return;
			if (e.key === 'Escape' && pendingPlacement) {
				e.preventDefault();
				cancelNodePlacement();
				return;
			}

			const ctrlOrMeta = e.ctrlKey || e.metaKey;
			const key = e.key?.toLowerCase?.() ?? '';

			if ((e.key === 'Delete' || e.key === 'Backspace') && !ctrlOrMeta) {
				e.preventDefault();
				const state = get(flowEditorState);
				if (state.selectedNodeIds.length) {
					flowEditorState.removeSelectedNodes();
				} else if (state.selectedNodeId) {
					flowEditorState.removeNode(state.selectedNodeId);
				}
				return;
			}

			if (!ctrlOrMeta) return;

			const state = get(flowEditorState);
			const hasNodeSelection = state.selectedNodeIds.length > 0 || state.selectedNodeId != null;

			if (key === 'c' && hasNodeSelection) {
				e.preventDefault();
				flowEditorState.copySelected();
				return;
			}
			if (key === 'x' && hasNodeSelection) {
				e.preventDefault();
				flowEditorState.cutSelected();
				return;
			}
			if (key === 'v') {
				e.preventDefault();
				flowEditorState.paste();
			}
		}

		// Capture so shortcuts work even when focus is not on the pane (unless typing in a field).
		document.addEventListener('keydown', handleKeyDown, true);

		const unsubscribeNodes = nodes.subscribe((nds) => {
			if (!initialized || isDragging || isConnecting) return;
			const selected = nds.filter((n) => n.selected).map((n) => n.id);
			const st = get(flowEditorState);
			if (sameIdSet(selected, st.selectedNodeIds)) return;
			flowEditorState.setSelectedNodeIds(selected);
		});

		return () => {
			registerFlowCanvasPositionFlush(null);
			registerFlowCanvasSync(null);
			unsubscribeEditor();
			unsubscribeEdgeBus();
			unsubscribeLayout();
			unsubscribeNodes();
			document.removeEventListener('keydown', handleKeyDown, true);
		};
	});

	onDestroy(() => {
		registerFlowCanvasPositionFlush(null);
		registerFlowCanvasSync(null);
	});

	// Watch for scene prop changes — re-initialize when switching flows or when *semantic/topology*
	// changes. Do NOT key off flowGraph.savedAt: it changes on every save and would re-run
	// initializeFromScene() while you still have the correct layout in memory.

	function normalizeFlowGraphCollection(input: any[] | Record<string, any> | undefined): any[] {
		if (!input) return [];
		if (Array.isArray(input)) return input;
		if (typeof input === 'object') {
			return Object.values(input).filter((item) => item && typeof item === 'object');
		}
		return [];
	}

	function flowGraphHasLayoutPositions(savedGraph: { nodes?: unknown } | undefined): boolean {
		const list = normalizeFlowGraphCollection(savedGraph?.nodes as any);
		return list.some(
			(n: any) => typeof n?.position?.x === 'number' && typeof n?.position?.y === 'number',
		);
	}

	function sceneReloadFingerprint(s: ISceneObj | null): string {
		if (!s) return '';
		const semantic = JSON.stringify({ if: s.if, do: s.do, else: s.else });
		const fg = (s as any)?._meta?.flowGraph;
		if (!fg?.nodes || !fg?.edges) {
			return semantic;
		}
		const nodes = normalizeFlowGraphCollection(fg.nodes);
		const edges = normalizeFlowGraphCollection(fg.edges);
		const nodeIds = [...new Set(nodes.map((n: any) => String(n?.id ?? '')))].filter(Boolean).sort();
		const edgeSig = edges
			.map((e: any) =>
				[e?.source ?? '', e?.sourceHandle ?? '', e?.target ?? '', e?.targetHandle ?? ''].join(
					'→',
				),
			)
			.sort();
		return `${semantic}|${JSON.stringify({ nodeIds, edgeSig })}`;
	}

	// Watch for scene prop changes — re-initialize when switching flows or when scene content changes
	$: sceneId = String(scene?._id ?? scene?.id ?? 'new');
	$: sceneContentKey = sceneReloadFingerprint(scene);
	$: if (
		scene &&
		(sceneId !== lastSceneId || sceneContentKey !== lastSceneContentKey || !initialized)
	) {
		lastSceneId = sceneId;
		lastSceneContentKey = sceneContentKey;
		initialized = false;
		// Drop stale XYFlow node positions before re-init (page reload / HMR can leave {0,0} coords).
		nodes.set([]);
		edges.set([]);
		initializeFromScene();
	}

	function handlePropertyChange(nodeId: string, newProperties: any) {
		const state = get(flowEditorState);
		const node = state.nodes.find((n) => n.id === nodeId);
		if (!node) return;
		flowEditorState.updateNode(nodeId, {
			data: { ...node.data, ...newProperties },
		});
		flowEditorState.update((s) => ({ ...s, propertyChanges: null }));
	}

	function initializeEmptyFlow() {
		flowEditorState.update((state) => ({
			...state,
			nodes: [],
			edges: [],
			selectedNodeId: null,
			selectedNodeIds: [],
			isEditorOpen: false,
		}));
		syncStoreToCanvasWritables();
		initialized = true;
	}

	function initializeFromScene() {
		if (!scene) {
			initializeEmptyFlow();
			dispatch('loadstatus', {
				source: 'reconstructed' as LoadStatusSource,
				message: 'Empty canvas',
				warnings: [],
				problematicNodes: [],
				skipPositionFlush: true,
			});
			return;
		}

		try {
			// Convert scene to graph
			const graph = sceneToGraph(scene);

			if (graph.nodes.length === 0) {
				initializeEmptyFlow();
				dispatch('loadstatus', {
					source: 'reconstructed' as LoadStatusSource,
					message: 'Empty flow',
					warnings: [],
					problematicNodes: [],
					skipPositionFlush: true,
				});
				return;
			}

			// Respect saved flowGraph positions; otherwise apply branch-aware auto-layout.
			const savedGraph = (scene as any)?._meta?.flowGraph;
			const savedNodeCount = Array.isArray(savedGraph?.nodes)
				? savedGraph.nodes.length
				: savedGraph?.nodes && typeof savedGraph.nodes === 'object'
					? Object.keys(savedGraph.nodes).length
					: 0;
			const hasSavedGraph = savedNodeCount > 0;
			const hasSavedLayout = hasSavedGraph && flowGraphHasLayoutPositions(savedGraph);
			const layouted = hasSavedLayout
				? { nodes: graph.nodes as Node[], edges: graph.edges as Edge[] }
				: applyBranchAwareLayout(graph.nodes as Node[], graph.edges as Edge[]);

			// Apply edge styling based on sourceHandle
			const styledEdges = applyEdgeStyles(layouted.edges);

			// Update the single source of truth (setNodesAndEdges applies per-node validation)
			flowEditorState.setNodesAndEdges(layouted.nodes as FlowNode[], styledEdges as FlowEdge[], {
				markClean: true,
			});
			// Apply positions immediately — do not wait for the reactive sync tick.
			syncStoreToCanvasWritables();

			const warnings: string[] = [];
			const source: LoadStatusSource = hasSavedGraph ? 'saved' : 'reconstructed';
			const message = source === 'saved' ? '' : 'Reconstructed from Flow data';

			if (!hasSavedGraph) {
				warnings.push('Flow layout was reconstructed from saved automation data.');
			}

			const problematicNodes = collectProblematicNodes(get(flowEditorState).nodes);
			const runtimeOrphans = problematicNodes.filter((p) => p.reason.includes('scene runtime'));
			if (runtimeOrphans.length > 0) {
				warnings.push(
					`${runtimeOrphans.length} node(s) were loaded without runtime mapping and may need reconfiguration.`,
				);
			} else if (problematicNodes.length > 0) {
				warnings.push(
					`${problematicNodes.length} node(s) need attention — see highlighted nodes on the canvas.`,
				);
			}

			dispatch('loadstatus', {
				source,
				message,
				warnings,
				problematicNodes,
				skipPositionFlush: true,
			});

			initialized = true;
		} catch (error) {
			console.error('Failed to convert scene to graph:', error);
			initializeEmptyFlow();
			dispatch('loadstatus', {
				source: 'reconstructed' as LoadStatusSource,
				message: 'Failed to parse Flow, showing an empty canvas',
				warnings: ['Flow parsing failed.'],
				problematicNodes: [],
				skipPositionFlush: true,
			});
		}
	}

	function applyEdgeStyles(edgeList: Edge[]): Edge[] {
		return edgeList.map((edge) => {
			let edgeClass = 'edge-default';
			let edgeColor: string = EDGE_COLORS.default;
			if (edge.sourceHandle === 'true') {
				edgeClass = 'edge-true';
				edgeColor = EDGE_COLORS.true;
			} else if (edge.sourceHandle === 'false') {
				edgeClass = 'edge-false';
				edgeColor = EDGE_COLORS.false;
			}

			return {
				...edge,
				type: 'midpoint',
				class: edgeClass,
				style: `stroke: ${edgeColor}; stroke-width: 2px;`,
				markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor, width: 18, height: 18 },
				animated: true,
			};
		});
	}

	function handleNodeDragStart() {
		isDragging = true;
		suppressNodeClickUntil = Date.now() + 250;
	}

	function handleNodeDrag(event: CustomEvent<{ targetNode: Node; nodes: Node[] }>) {
		const dragged = event.detail?.targetNode;
		if (!dragged) return;
		const liveNodes = get(nodes);
		const center = nodeCenter(dragged);
		let closest: Node | null = null;
		let closestDistance = PROXIMITY_DISTANCE;
		for (const candidate of liveNodes) {
			if (candidate.id === dragged.id) continue;
			const other = nodeCenter(candidate);
			const distance = Math.hypot(center.x - other.x, center.y - other.y);
			if (distance < closestDistance) {
				closest = candidate;
				closestDistance = distance;
			}
		}
		if (!closest) {
			proximityConnection = null;
			edges.update((items) => items.filter((edge) => edge.id !== PROXIMITY_EDGE_ID));
			return;
		}
		const closestCenter = nodeCenter(closest);
		const source = center.x <= closestCenter.x ? dragged : closest;
		const target = source.id === dragged.id ? closest : dragged;
		if (String(target.type) === 'trigger') return;
		const connection: Connection = {
			source: source.id,
			target: target.id,
			sourceHandle: preferredSourceHandle(source),
			targetHandle: 'input',
		};
		const exists = get(flowEditorState).edges.some(
			(edge) => edge.source === connection.source && edge.target === connection.target,
		);
		if (exists) return;
		proximityConnection = connection;
		const preview: Edge = {
			id: PROXIMITY_EDGE_ID,
			...connection,
			type: 'midpoint',
			animated: false,
			class: 'edge-proximity-preview',
			style: 'stroke: #60a5fa; stroke-width: 2px; stroke-dasharray: 7 6;',
			markerEnd: { type: MarkerType.ArrowClosed, color: '#60a5fa', width: 18, height: 18 },
		};
		edges.update((items) => [...items.filter((edge) => edge.id !== PROXIMITY_EDGE_ID), preview]);
	}

	function nodeCenter(node: Node) {
		return {
			x: (node.position?.x ?? 0) + Number(node.measured?.width ?? node.width ?? 320) / 2,
			y: (node.position?.y ?? 0) + Number(node.measured?.height ?? node.height ?? 100) / 2,
		};
	}

	function preferredSourceHandle(node: Node): string {
		const type = String(node.type || '');
		if (type === 'condition' || type.endsWith('Condition') || type === 'trigger' || type === 'timer')
			return 'true';
		return 'output';
	}

	function handleNodeDragStop() {
		isDragging = false;
		// Copy positions from the live XYFlow store (handles reference quirks after drag).
		flushLiveNodePositionsToStore();
		const connection = proximityConnection;
		proximityConnection = null;
		edges.update((items) => items.filter((edge) => edge.id !== PROXIMITY_EDGE_ID));
		if (connection) handleConnect(connection);
		syncStoreToCanvasWritables();
	}

	/** Copy live SvelteFlow node positions into flowEditorState (needed if user saves mid-drag). */
	function flushLiveNodePositionsToStore() {
		const live = get(nodes) as Node[];
		if (live.length === 0) return;
		flowEditorState.update((s) => {
			const liveById = new Map(live.map((n) => [n.id, n]));
			return {
				...s,
				nodes: s.nodes.map((n) => {
					const ln = liveById.get(n.id);
					if (!ln?.position) return n;
					return {
						...n,
						position: { x: ln.position.x, y: ln.position.y },
					};
				}),
			};
		});
	}

	function handleDrop(event: DragEvent) {
		event.preventDefault();

		if (!event.dataTransfer) return;

		const nodeType = event.dataTransfer.getData('application/nodeType');
		if (!nodeType) return;
		const paletteKey = event.dataTransfer.getData('application/paletteKey') || nodeType;

		const toFlow = get(screenToFlowPositionFn);
		const position = toFlow
			? toFlow({ x: event.clientX, y: event.clientY })
			: { x: event.clientX, y: event.clientY };

		const newNode: Node = {
			id: generateUniqueNodeId(),
			type: nodeType,
			position,
			data: buildDefaultNodeData(nodeType, paletteKey),
		};

		flowEditorState.addNode(newNode as FlowNode);
		trackFlowUxEvent({
			name: 'placement_completed',
			surface: 'canvas',
			paletteKey,
			kind: 'node',
		});
		flushLiveFlowCanvasSync();
	}

	function handleDragOver(event: DragEvent) {
		event.preventDefault();
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = 'copy';
		}
	}

	function handleNodeClick(event: CustomEvent) {
		if (pendingPlacement) return;
		if (isDragging || Date.now() < suppressNodeClickUntil) return;
		const detail = event.detail as { node: Node; event?: MouseEvent | TouchEvent };
		const nodeId = detail.node.id;
		const ev = detail.event;
		const eventTarget = ev?.target;
		if (eventTarget instanceof Element && eventTarget.closest('.svelte-flow__handle')) return;
		const additive = !!(ev && 'ctrlKey' in ev && (ev.ctrlKey || ev.metaKey));
		suppressPaneClickUntil = Date.now() + 180;
		flowEditorState.selectNode(nodeId, { additive });
		flowEditorState.setEditorOpen(!additive);
		flowEditorState.clearSimulationHighlight();
		paneMenuVisible = false;
		quickNodesVisible = false;
		nodeCtxVisible = false;
	}

	function placePendingNodeAt(clientX: number, clientY: number): boolean {
		if (!pendingPlacement) return false;
		const placement = pendingPlacement;
		const toFlow = get(screenToFlowPositionFn);
		const point = toFlow ? toFlow({ x: clientX, y: clientY }) : { x: clientX, y: clientY };
		if (placement.templateId) {
			const template = getFlowTemplate(placement.templateId);
			if (!template) return false;
			const ids = new Map(template.nodes.map((node) => [node.key, generateUniqueNodeId()]));
			const newNodes = template.nodes.map((node) => ({
				id: ids.get(node.key)!,
				type: node.type,
				position: { x: point.x + node.offset.x - 100, y: point.y + node.offset.y - 36 },
				data: {
					...buildDefaultNodeData(node.type, node.paletteKey ?? node.type),
					...(node.data ? structuredClone(node.data) : {}),
				},
				validated: true,
				errors: [],
			})) as FlowNode[];
			const newEdges = template.edges.map((edge, index) => ({
				id: `template-edge-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
				source: ids.get(edge.from)!,
				target: ids.get(edge.to)!,
				sourceHandle: edge.branch,
				targetHandle: 'target',
				type: 'midpoint',
			})) as FlowEdge[];
			trackFlowUxEvent({
				name: 'placement_completed',
				surface: 'canvas',
				paletteKey: placement.templateId,
				kind: 'template',
			});
			pendingPlacement = null;
			placementPointerVisible = false;
			flowEditorState.addGraph(newNodes, newEdges);
			flushLiveFlowCanvasSync();
			flowEditorState.setEditorOpen(false);
			return true;
		}
		if (!placement.type || !placement.paletteKey) return false;
		const newNode: Node = {
			id: generateUniqueNodeId(),
			type: placement.type,
			// XY Flow stores the card's top-left corner; offset so the chosen point is its center.
			position: { x: point.x - 100, y: point.y - 36 },
			data: {
				...buildDefaultNodeData(placement.type, placement.paletteKey),
				...(placement.initialData ? structuredClone(placement.initialData) : {}),
			},
		};
		trackFlowUxEvent({
			name: 'placement_completed',
			surface: 'canvas',
			paletteKey: placement.paletteKey,
			kind: 'node',
		});
		pendingPlacement = null;
		placementPointerVisible = false;
		flowEditorState.addNode(newNode as FlowNode);
		flushLiveFlowCanvasSync();
		// Let XY Flow finish its pane-click deselection before selecting the new node.
		flowEditorState.setEditorOpen(false);
		return true;
	}

	function handlePlacementCanvasClick(event: MouseEvent) {
		if (!pendingPlacement) return;
		const target = event.target as HTMLElement | null;
		if (
			target?.closest(
				'.placement-banner, .svelte-flow__node, .svelte-flow__edge, .svelte-flow__controls, .svelte-flow__minimap, button, input, textarea, a',
			)
		) {
			return;
		}
		placePendingNodeAt(event.clientX, event.clientY);
	}

	function handlePaneClick(event: CustomEvent<{ event: MouseEvent | TouchEvent }>) {
		if (Date.now() < suppressPaneClickUntil) return;
		if (pendingPlacement) {
			const { x: clientX, y: clientY } = connectEndClientPos(event.detail.event);
			placePendingNodeAt(clientX, clientY);
			return;
		}
		flowEditorState.setEditorOpen(false);
		flowEditorState.selectNode(null);
		flowEditorState.clearSimulationHighlight();
		paneMenuVisible = false;
		quickNodesVisible = false;
		nodeCtxVisible = false;
	}

	function handleConnect(connection: Connection) {
		// A completed connection must never fall through to the node click handler.
		suppressNodeClickUntil = Date.now() + 700;
		suppressPaneClickUntil = Date.now() + 700;
		// Determine edge color and class based on source handle
		let edgeClass = 'edge-default';
		let edgeColor: string = EDGE_COLORS.default;

		if (connection.sourceHandle === 'true') {
			edgeClass = 'edge-true';
			edgeColor = EDGE_COLORS.true;
		} else if (connection.sourceHandle === 'false') {
			edgeClass = 'edge-false';
			edgeColor = EDGE_COLORS.false;
		}

		const newEdge: Edge = {
			id: `e-${connection.source}-${connection.target}-${connection.sourceHandle || 'default'}`,
			source: connection.source,
			target: connection.target,
			sourceHandle: connection.sourceHandle,
			targetHandle: connection.targetHandle,
			animated: true,
			type: 'midpoint',
			class: edgeClass,
			style: `stroke: ${edgeColor}; stroke-width: 2px;`,
			markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor, width: 18, height: 18 },
		};

		// Add to single source of truth - local stores update via reactive block
		flowEditorState.addEdge(newEdge as FlowEdge);
		endConnectionDrag(false);
	}

	function connectEndClientPos(event: MouseEvent | TouchEvent): { x: number; y: number } {
		if ('clientX' in event) {
			return { x: event.clientX, y: event.clientY };
		}
		const t = event.changedTouches?.[0];
		return t ? { x: t.clientX, y: t.clientY } : { x: 0, y: 0 };
	}

	function handleConnectEnd(event: MouseEvent | TouchEvent, connectionState: FinalConnectionState) {
		suppressNodeClickUntil = Date.now() + 700;
		suppressPaneClickUntil = Date.now() + 700;
		if (!connectionState.fromHandle) {
			endConnectionDrag(true);
			return;
		}

		if (connectionState.toHandle != null) {
			endConnectionDrag(true);
			return;
		}

		const drop = connectionState.to;
		if (!drop) {
			endConnectionDrag(true);
			return;
		}

		const fromNode = connectionState.fromNode;
		const source = fromNode?.id ?? connectionState.fromHandle.nodeId;
		const sourceHandle =
			connectionState.fromHandle.id !== undefined && connectionState.fromHandle.id !== null
				? String(connectionState.fromHandle.id)
				: null;
		const endPoint = connectEndClientPos(event);
		const startPoint = connectStartPoint;
		connectStartPoint = null;
		if (startPoint && Math.hypot(endPoint.x - startPoint.x, endPoint.y - startPoint.y) < 8) {
			const canvasRect = document.querySelector('.flow-canvas')?.getBoundingClientRect();
			armedConnection = {
				source,
				sourceHandle,
				x: startPoint.x - (canvasRect?.left ?? 0),
				y: startPoint.y - (canvasRect?.top ?? 0),
			};
			armedPointer = {
				x: endPoint.x - (canvasRect?.left ?? 0),
				y: endPoint.y - (canvasRect?.top ?? 0),
			};
			endConnectionDrag(true);
			return;
		}

		contextualPlacement = { x: drop.x, y: drop.y, source, sourceHandle };
		dispatch('requestadd', { entryPoint: 'connection' });
		suppressPaneClickUntil = Date.now() + 400;
		endConnectionDrag(true);
	}

	function handleArmedConnectionPointerDown(event: MouseEvent) {
		if (!armedConnection) return;
		const handle = (event.target as HTMLElement | null)?.closest(
			'.svelte-flow__handle',
		) as HTMLElement | null;
		if (!handle) return;
		const target = handle.dataset.nodeid;
		if (!target || target === armedConnection.source || !handle.classList.contains('target')) return;
		event.preventDefault();
		event.stopPropagation();
		handleConnect({
			source: armedConnection.source,
			target,
			sourceHandle: armedConnection.sourceHandle,
			targetHandle: handle.dataset.handleid || 'input',
		});
		armedConnection = null;
	}

	function openQuickNodesPanel(
		screenX: number,
		screenY: number,
		flowX: number,
		flowY: number,
		purpose: 'free' | 'connect',
	) {
		quickNodesScreenX = screenX;
		quickNodesScreenY = screenY;
		quickFlowX = flowX;
		quickFlowY = flowY;
		quickPlacementPurpose = purpose;
		if (purpose === 'connect') {
			quickTitle = 'Add next card';
			quickHint = 'Pick a node to create here; it will connect from the handle you dragged.';
		} else {
			quickTitle = 'Add node';
			quickHint = 'Search or pick a node type to place at this spot on the canvas.';
		}
		quickNodesVisible = true;
		paneMenuVisible = false;
		nodeCtxVisible = false;
	}

	function handleQuickPick(
		event: CustomEvent<{ type: string; paletteKey: string; x: number; y: number }>,
	) {
		const { type, paletteKey, x, y } = event.detail;
		const pending = pendingConnectFrom;
		pendingConnectFrom = null;
		quickNodesVisible = false;

		const newNode: Node = {
			id: generateUniqueNodeId(),
			type,
			position: { x, y },
			data: buildDefaultNodeData(type, paletteKey),
		};
		flowEditorState.addNode(newNode as FlowNode);
		flushLiveFlowCanvasSync();

		if (pending) {
			handleConnect({
				source: pending.source,
				target: newNode.id,
				sourceHandle: pending.sourceHandle,
				targetHandle: 'input',
			});
		}
	}

	function handleQuickCancel() {
		pendingConnectFrom = null;
	}

	function openPaneContextMenu(clientX: number, clientY: number) {
		const fn = get(screenToFlowPositionFn);
		if (!fn) return;
		const flowPos = fn({ x: clientX, y: clientY });
		addAtFlowX = flowPos.x;
		addAtFlowY = flowPos.y;
		paneMenuX = clientX;
		paneMenuY = clientY;
		paneMenuVisible = true;
		quickNodesVisible = false;
		nodeCtxVisible = false;
	}

	function handlePaneContextMenu(event: CustomEvent | MouseEvent) {
		const e =
			'detail' in event && event.detail?.event
				? (event.detail.event as MouseEvent)
				: (event as MouseEvent);
		e.preventDefault();
		openPaneContextMenu(e.clientX, e.clientY);
	}

	function handlePaneMenuAddNode() {
		contextualPlacement = { x: addAtFlowX, y: addAtFlowY };
		paneMenuVisible = false;
		dispatch('requestadd', { entryPoint: 'canvas' });
	}

	function handleNodeContextMenu(event: CustomEvent<{ node: Node; event: MouseEvent | TouchEvent }>) {
		const { node, event: ev } = event.detail;
		if ('preventDefault' in ev) ev.preventDefault();
		nodeCtxTargetId = node.id;
		if ('clientX' in ev) {
			nodeCtxX = ev.clientX;
			nodeCtxY = ev.clientY;
		} else {
			const t = ev.changedTouches?.[0];
			nodeCtxX = t?.clientX ?? 0;
			nodeCtxY = t?.clientY ?? 0;
		}
		nodeCtxVisible = true;
		paneMenuVisible = false;
		quickNodesVisible = false;
	}

	function handleNodeCtxEdit() {
		const id = nodeCtxTargetId;
		nodeCtxTargetId = null;
		if (!id) return;
		flowEditorState.selectNode(id);
		flowEditorState.setEditorOpen(true);
		flowEditorState.clearSimulationHighlight();
	}

	function handleNodeCtxCopy() {
		const id = nodeCtxTargetId;
		nodeCtxTargetId = null;
		if (!id) return;
		flowEditorState.selectNode(id);
		flowEditorState.copySelected();
	}

	function handleNodeCtxCut() {
		const id = nodeCtxTargetId;
		nodeCtxTargetId = null;
		if (!id) return;
		flowEditorState.selectNode(id);
		flowEditorState.cutSelected();
	}

	function handleNodeCtxDelete() {
		const id = nodeCtxTargetId;
		nodeCtxTargetId = null;
		if (!id) return;
		flowEditorState.removeNode(id);
	}

	function referenceValues(value: unknown, path = '', found = new Set<string>()): Set<string> {
		if (Array.isArray(value)) value.forEach((item) => referenceValues(item, path, found));
		else if (value && typeof value === 'object')
			Object.entries(value as Record<string, unknown>).forEach(([childKey, child]) =>
				referenceValues(child, path ? `${path}.${childKey}` : childKey, found),
			);
		else if (
			(typeof value === 'string' || typeof value === 'number') &&
			/(device.*id|parentDeviceId|variableName|sourceRef|scene.*id|group.*id|script.*id|\.ref)$/i.test(
				path,
			)
		)
			found.add(String(value));
		return found;
	}

	function handleNodeCtxFindUsages() {
		const id = nodeCtxTargetId;
		nodeCtxTargetId = null;
		const st = get(flowEditorState);
		const target = st.nodes.find((node) => node.id === id);
		if (!target) return;
		const refs = referenceValues(target.data);
		const matches = st.nodes
			.filter((node) => [...referenceValues(node.data)].some((ref) => refs.has(ref)))
			.map((node) => node.id);
		flowEditorState.setSelectedNodeIds(matches.length ? matches : [target.id]);
	}

	function replaceDeviceIds(value: unknown, oldId: string, newId: string, path = ''): unknown {
		if (Array.isArray(value)) return value.map((item) => replaceDeviceIds(item, oldId, newId, path));
		if (value && typeof value === 'object')
			return Object.fromEntries(
				Object.entries(value as Record<string, unknown>).map(([childKey, child]) => [
					childKey,
					replaceDeviceIds(child, oldId, newId, path ? `${path}.${childKey}` : childKey),
				]),
			);
		return /device.*id|parentDeviceId/i.test(path) && String(value) === oldId ? newId : value;
	}

	function findDeviceId(value: unknown, path = ''): string | undefined {
		if (Array.isArray(value)) {
			for (const item of value) {
				const match = findDeviceId(item, path);
				if (match) return match;
			}
		} else if (value && typeof value === 'object') {
			for (const [childKey, child] of Object.entries(value as Record<string, unknown>)) {
				const match = findDeviceId(child, path ? `${path}.${childKey}` : childKey);
				if (match) return match;
			}
		} else if (/device.*id|parentDeviceId/i.test(path) && value != null) return String(value);
		return undefined;
	}

	function handleNodeCtxReplaceDevice() {
		const id = nodeCtxTargetId;
		nodeCtxTargetId = null;
		const st = get(flowEditorState);
		const target = st.nodes.find((node) => node.id === id);
		if (!target) return;
		const oldId = findDeviceId(target.data);
		if (!oldId) return window.alert('This card has no replaceable device reference.');
		const newId = window.prompt(`Replace device ${oldId} with device ID:`, '');
		if (!newId?.trim() || !window.confirm(`Replace ${oldId} everywhere in this flow?`)) return;
		flowEditorState.setNodesAndEdges(
			st.nodes.map((node) => ({
				...node,
				data: replaceDeviceIds(node.data, oldId, newId.trim()) as typeof node.data,
			})),
			st.edges,
			{ markDirty: true },
		);
	}

	function handleNodeCtxExtractSubflow() {
		const id = nodeCtxTargetId;
		nodeCtxTargetId = null;
		const st = get(flowEditorState);
		const selected = st.selectedNodeIds.includes(String(id)) ? st.selectedNodeIds : [String(id)];
		const name = window.prompt('Reusable subflow name:', 'Reusable automation');
		if (name === null) return;
		saveReusableSubflow(createReusableSubflow(name, st.nodes, st.edges, selected));
	}

	function handlePaneTouchEnd(e: TouchEvent) {
		if (pendingPlacement) return;
		const now = Date.now();
		if (now - lastTapTime < 350 && e.changedTouches.length === 1) {
			const t = e.changedTouches[0];
			openPaneContextMenu(t.clientX, t.clientY);
		}
		lastTapTime = now;
	}

	function handleEdgeMidpointInsert(
		edgeId: string,
		x: number,
		y: number,
		nodeType: string,
		paletteKey = nodeType,
		initialData?: Record<string, unknown>,
	) {
		const state = get(flowEditorState);
		const edge = state.edges.find((e) => e.id === edgeId);
		if (!edge) return;

		// Prevent inserting into a self-loop edge (source === target)
		if (edge.source === edge.target) return;

		// Keep generating until we get an ID that doesn't already exist (guards against any
		// edge-case collision, e.g. handler firing twice before the bus is cleared)
		let newNodeId = generateUniqueNodeId();
		while (state.nodes.some((n) => n.id === newNodeId)) {
			newNodeId = generateUniqueNodeId();
		}

		const newNode: FlowNode = {
			id: newNodeId,
			type: nodeType as any,
			position: { x, y },
			data: {
				...buildDefaultNodeData(nodeType, paletteKey),
				...(initialData ? structuredClone(initialData) : {}),
			},
			validated: true,
			errors: [],
		} as FlowNode;

		const edge1: FlowEdge = {
			id: `e-${edge.source}-${newNodeId}-${edge.sourceHandle || 'default'}`,
			source: edge.source,
			target: newNodeId,
			sourceHandle: edge.sourceHandle,
			targetHandle: 'input',
			animated: true,
			type: 'midpoint',
			class: (edge as any).class,
			style: (edge as any).style,
		} as FlowEdge;

		const edge2: FlowEdge = {
			id: `e-${newNodeId}-${edge.target}-output`,
			source: newNodeId,
			target: edge.target,
			sourceHandle: 'output',
			targetHandle: edge.targetHandle,
			animated: true,
			type: 'midpoint',
		} as FlowEdge;

		flowEditorState.update((s) => {
			// Deduplicate: drop any existing edges with the same ID as the new ones
			const filteredEdges = s.edges.filter(
				(e) => e.id !== edgeId && e.id !== edge1.id && e.id !== edge2.id,
			);
			// Also ensure the new node ID is truly absent (double-fire guard)
			if (s.nodes.some((n) => n.id === newNodeId)) return s;
			return {
				...s,
				nodes: [...s.nodes, newNode],
				edges: [...filteredEdges, edge1, edge2],
				isDirty: true,
			};
		});
	}
</script>

<div
	class="flow-canvas"
	class:semantic-compact={semanticZoom === 'compact'}
	class:semantic-overview={semanticZoom === 'overview'}
	class:placing-node={pendingPlacement}
	class:canvas-theme-dark={$flowCanvasTheme === 'dark'}
	class:canvas-theme-light={$flowCanvasTheme === 'light'}
	on:drop={handleDrop}
	on:dragover={handleDragOver}
	on:click={handlePlacementCanvasClick}
	on:pointermove={handlePlacementPointerMove}
	on:mousedown|capture={handleArmedConnectionPointerDown}
	on:pointerleave={() => (placementPointerVisible = false)}
	on:touchend={handlePaneTouchEnd}
	role="application"
	aria-label="Flow editor canvas"
	use:observeSemanticZoom
>
	{#if armedConnection}
		<svg class="armed-connection-preview" aria-hidden="true">
			<line
				x1={armedConnection.x}
				y1={armedConnection.y}
				x2={armedPointer.x}
				y2={armedPointer.y}
			/>
		</svg>
		<div class="armed-connection-hint">
			Choose an input port <button type="button" on:click={() => (armedConnection = null)}
				>Cancel</button
			>
		</div>
	{/if}
	{#if pendingPlacement}
		<div class="placement-banner" role="status" aria-live="polite">
			<span class="placement-banner-icon" style="--placement-color: {pendingPlacement.color}">
				<i class="fas {pendingPlacement.icon}" aria-hidden="true" />
			</span>
			<span>
				<strong>Place {pendingPlacement.label}</strong>
				<span class="placement-help">Click or tap an empty canvas position.</span>
			</span>
			<button type="button" on:click={cancelNodePlacement} aria-label="Cancel node placement">
				Cancel <kbd>Esc</kbd>
			</button>
		</div>
		{#if placementPointerVisible}
			<div
				class="placement-ghost"
				style="left: {placementPointer.x}px; top: {placementPointer.y}px; --placement-color: {pendingPlacement.color}"
				aria-hidden="true"
			>
				<i class="fas {pendingPlacement.icon}" />
				<span>{pendingPlacement.label}</span>
			</div>
		{/if}
	{/if}
	{#if islandGroups.length > 1}
		<div class="island-manager">
			<button
				type="button"
				class="island-manager-trigger"
				aria-expanded={showIslandManager}
				on:click={() => (showIslandManager = !showIslandManager)}
				><i class="fas fa-object-group"></i>{islandGroups.length} automations</button
			>
			{#if showIslandManager}
				<div class="island-manager-popover">
					<div>
						<strong>Flow automations</strong><span
							>Each disconnected section counts as an island.</span
						>
					</div>
					{#each islandGroups as island}
						<div class="island-row">
							<button
								type="button"
								on:click={() => toggleIsland(island.id)}
								aria-label={collapsedIslandIds.has(island.id)
									? `Expand ${island.name}`
									: `Collapse ${island.name}`}
								><i
									class="fas {collapsedIslandIds.has(island.id)
										? 'fa-chevron-right'
										: 'fa-chevron-down'}"
								></i></button
							><input
								value={island.name}
								aria-label="Automation name"
								on:change={(event) => renameIsland(island.rootNodeId, event)}
							/><span>{island.nodeIds.length} cards</span>
						</div>
					{/each}
				</div>
			{/if}
		</div>
	{/if}
	<!-- Mobile accepts primary touch/pointer panning; desktop keeps left-drag marquee selection. -->
	<SvelteFlow
		{nodes}
		{edges}
		nodeTypes={nodeTypesMap}
		{edgeTypes}
		fitView
		fitViewOptions={{ minZoom: READABLE_FIT_MIN_ZOOM, maxZoom: 0.75, padding: 0.2 }}
		snapGrid={[15, 15]}
		connectionRadius={36}
		nodesConnectable={true}
		nodesDraggable={true}
		nodeClickDistance={NODE_CLICK_DISTANCE}
		panOnDrag={isMobile ? [0, 1, 2] : [1, 2]}
		selectionOnDrag={!isMobile}
		zoomOnPinch={true}
		proOptions={{ hideAttribution: true }}
		defaultEdgeOptions={{
			type: 'midpoint',
			animated: true,
		}}
		on:nodeclick={handleNodeClick}
		on:paneclick={handlePaneClick}
		on:nodedragstart={handleNodeDragStart}
		on:nodedrag={handleNodeDrag}
		on:nodedragstop={handleNodeDragStop}
		on:panecontextmenu={handlePaneContextMenu}
		on:nodecontextmenu={handleNodeContextMenu}
		onconnectstart={handleConnectStart}
		onconnect={handleConnect}
		onconnectend={handleConnectEnd}
		bind:this={flowInstance}
	>
		<Background variant={bgVariant} gap={16} size={1} />
		<Controls />
		<CanvasContextBridge screenToFlowPositionStore={screenToFlowPositionFn} />
		{#if !isMobile}
			<MiniMap
				nodeColor={(node) => String(node.data?.color || '#666')}
				maskColor="rgba(0, 0, 0, 0.5)"
			/>
		{/if}
	</SvelteFlow>
	{#if $flowEditorState.edges.length > 0 && semanticZoom !== 'overview'}
		<div class="edge-legend" aria-label="Connection legend">
			<span><i class="edge-dot edge-dot-true"></i>If true</span>
			<span><i class="edge-dot edge-dot-false"></i>If false</span>
			<span><i class="edge-dot edge-dot-next"></i>Then / continue</span>
		</div>
	{/if}
</div>

<FlowPaneContextMenu
	bind:visible={paneMenuVisible}
	x={paneMenuX}
	y={paneMenuY}
	canPaste={canFlowPaste}
	on:addNode={handlePaneMenuAddNode}
	on:paste={() => flowEditorState.paste()}
	on:cancel={() => (paneMenuVisible = false)}
/>

<QuickNodesMenu
	bind:visible={quickNodesVisible}
	placementPurpose={quickPlacementPurpose}
	x={quickNodesScreenX}
	y={quickNodesScreenY}
	flowX={quickFlowX}
	flowY={quickFlowY}
	title={quickTitle}
	hint={quickHint}
	on:pick={handleQuickPick}
	on:cancel={handleQuickCancel}
/>

<FlowNodeContextMenu
	bind:visible={nodeCtxVisible}
	x={nodeCtxX}
	y={nodeCtxY}
	on:edit={handleNodeCtxEdit}
	on:copy={handleNodeCtxCopy}
	on:cut={handleNodeCtxCut}
	on:findUsages={handleNodeCtxFindUsages}
	on:replaceDevice={handleNodeCtxReplaceDevice}
	on:extractSubflow={handleNodeCtxExtractSubflow}
	on:delete={handleNodeCtxDelete}
	on:cancel={() => (nodeCtxVisible = false)}
/>

<style>
	.armed-connection-preview {
		position: absolute;
		inset: 0;
		z-index: 20;
		width: 100%;
		height: 100%;
		pointer-events: none;
		overflow: visible;
	}
	.armed-connection-preview line {
		stroke: #60a5fa;
		stroke-width: 2;
		stroke-dasharray: 7 6;
	}
	.armed-connection-hint {
		position: absolute;
		top: 12px;
		left: 50%;
		z-index: 21;
		transform: translateX(-50%);
		padding: 7px 10px;
		border: 1px solid rgba(96, 165, 250, 0.45);
		border-radius: 999px;
		background: rgba(15, 23, 42, 0.92);
		color: #e2e8f0;
		font-size: 11px;
	}
	.armed-connection-hint button {
		margin-left: 8px;
		border: 0;
		background: transparent;
		color: #93c5fd;
		cursor: pointer;
	}
	.flow-canvas {
		width: 100%;
		height: 100%;
		background: #111720;
		position: relative;
	}

	.flow-canvas.semantic-compact :global(.flow-role-chip span),
	.flow-canvas.semantic-compact :global(.flow-node-card__body > :not(.flow-node-card__sentence)),
	.flow-canvas.semantic-compact :global(.flow-node-advanced-card .description),
	.flow-canvas.semantic-overview :global(.flow-role-chip span),
	.flow-canvas.semantic-overview :global(.flow-node-card__body),
	.flow-canvas.semantic-overview :global(.flow-node-advanced-card .description),
	.flow-canvas.semantic-overview :global(.flow-node-advanced-card .subtitle),
	.flow-canvas.semantic-overview :global(.handle-label),
	.flow-canvas.semantic-overview :global(.error-indicator span),
	.flow-canvas.semantic-overview :global(.error-indicator em) {
		display: none !important;
	}

	.flow-canvas.semantic-overview :global(.flow-node-card-root) {
		width: 14rem;
		min-width: 14rem;
	}

	.edge-legend {
		position: absolute;
		left: 12px;
		bottom: 12px;
		z-index: 12;
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 6px 9px;
		border: 1px solid rgba(148, 163, 184, 0.18);
		border-radius: 999px;
		background: rgba(17, 24, 39, 0.82);
		color: #cbd5e1;
		font-size: 9px;
		backdrop-filter: blur(8px);
	}

	.island-manager {
		position: absolute;
		top: 12px;
		right: 12px;
		z-index: 26;
	}
	.island-manager-trigger {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		border: 1px solid rgba(167, 139, 250, 0.3);
		border-radius: 8px;
		background: rgba(30, 27, 45, 0.9);
		color: #ddd6fe;
		padding: 7px 9px;
		font-size: 10px;
		cursor: pointer;
	}
	.island-manager-popover {
		position: absolute;
		top: calc(100% + 6px);
		right: 0;
		display: grid;
		gap: 7px;
		width: 280px;
		padding: 10px;
		border: 1px solid rgba(167, 139, 250, 0.3);
		border-radius: 10px;
		background: #202129;
		box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
	}
	.island-manager-popover > div:first-child {
		display: flex;
		flex-direction: column;
	}
	.island-manager-popover > div:first-child strong {
		color: #f1f5f9;
		font-size: 12px;
	}
	.island-manager-popover > div:first-child span {
		color: #94a3b8;
		font-size: 9px;
	}
	.island-row {
		display: grid;
		grid-template-columns: 24px minmax(0, 1fr) auto;
		gap: 6px;
		align-items: center;
	}
	.island-row button {
		border: 0;
		background: transparent;
		color: #a78bfa;
		cursor: pointer;
	}
	.island-row input {
		min-width: 0;
		border: 1px solid rgba(148, 163, 184, 0.16);
		border-radius: 6px;
		background: rgba(148, 163, 184, 0.05);
		color: #e2e8f0;
		padding: 6px;
		font-size: 10px;
	}
	.island-row span {
		color: #94a3b8;
		font-size: 8px;
		white-space: nowrap;
	}

	.edge-legend span {
		display: inline-flex;
		align-items: center;
		gap: 4px;
	}
	.edge-dot {
		width: 12px;
		height: 2px;
		border-radius: 2px;
		background: #94a3b8;
	}
	.edge-dot-true {
		background: #22c55e;
	}
	.edge-dot-false {
		background: #ef4444;
	}
	.edge-dot-next {
		background: #60a5fa;
	}

	@media (max-width: 639px) {
		.edge-legend {
			left: 8px;
			right: 8px;
			bottom: 8px;
			justify-content: center;
		}
	}

	.flow-canvas.placing-node,
	.flow-canvas.placing-node :global(.svelte-flow__pane) {
		cursor: crosshair;
	}

	.placement-banner {
		position: absolute;
		top: 12px;
		left: 50%;
		transform: translateX(-50%);
		z-index: 30;
		display: flex;
		align-items: center;
		gap: 10px;
		max-width: calc(100% - 24px);
		padding: 8px 10px;
		border: 1px solid rgba(96, 165, 250, 0.42);
		border-radius: 10px;
		background: rgba(15, 23, 42, 0.94);
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.32);
		color: #f8fafc;
		font-size: 12px;
		pointer-events: auto;
	}

	.placement-banner > span:nth-child(2) {
		display: flex;
		flex-direction: column;
		min-width: 0;
	}

	.placement-banner-icon {
		display: grid;
		place-items: center;
		width: 28px;
		height: 28px;
		flex: 0 0 28px;
		border-radius: 8px;
		background: color-mix(in srgb, var(--placement-color) 24%, transparent);
		color: var(--placement-color);
	}

	.placement-help {
		color: #94a3b8;
		white-space: nowrap;
	}

	.placement-banner button {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		flex-shrink: 0;
		margin-left: 8px;
		padding: 6px 8px;
		border: 0;
		border-radius: 7px;
		background: rgba(255, 255, 255, 0.08);
		color: #e2e8f0;
		cursor: pointer;
		white-space: nowrap;
	}

	.placement-banner kbd {
		padding: 1px 4px;
		border: 1px solid rgba(255, 255, 255, 0.18);
		border-radius: 4px;
		font: inherit;
		font-size: 10px;
	}

	.placement-ghost {
		position: absolute;
		z-index: 25;
		transform: translate(-50%, -50%);
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 150px;
		max-width: 220px;
		padding: 10px 12px;
		border: 1px dashed var(--placement-color);
		border-radius: 9px;
		background: rgba(15, 23, 42, 0.84);
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
		color: #f8fafc;
		font-size: 12px;
		pointer-events: none;
	}

	.placement-ghost i {
		color: var(--placement-color);
	}

	@media (max-width: 639px) {
		.placement-banner {
			top: 8px;
			left: 8px;
			right: 8px;
			transform: none;
		}

		.placement-help {
			white-space: normal;
		}

		.placement-banner button kbd {
			display: none;
		}
	}

	.flow-canvas.canvas-theme-dark {
		background: #111720;
	}

	.flow-canvas.canvas-theme-light {
		background: #fbfbfa;
	}

	.flow-canvas.canvas-theme-dark :global(.svelte-flow) {
		background: #111720;
	}

	.flow-canvas.canvas-theme-light :global(.svelte-flow) {
		background: #fbfbfa;
	}

	/*
	 * XY Flow defaults both .svelte-flow__nodes and .svelte-flow__edgelabel-renderer to z-index: 0,
	 * so paint order can put graph nodes on top of edge midpoint handles/menus (EdgeLabelRenderer).
	 */
	.flow-canvas :global(.svelte-flow__edgelabel-renderer) {
		z-index: 10;
	}

	.flow-canvas.canvas-theme-dark :global(.svelte-flow__background) {
		background: #111720 !important;
	}

	.flow-canvas.canvas-theme-light :global(.svelte-flow__background) {
		background: #fbfbfa !important;
	}

	.flow-canvas.canvas-theme-dark :global(.svelte-flow__controls) {
		bottom: 20px;
		left: 20px;
	}

	.flow-canvas.canvas-theme-light :global(.svelte-flow__controls) {
		bottom: 20px;
		left: 20px;
	}

	.flow-canvas.canvas-theme-dark :global(.svelte-flow__controls button) {
		background: rgba(20, 27, 36, 0.9);
		border: 1px solid rgba(148, 163, 184, 0.25);
		color: #cbd5e1;
	}

	.flow-canvas.canvas-theme-dark :global(.svelte-flow__controls button:hover) {
		background: rgba(30, 41, 59, 0.95);
	}

	.flow-canvas.canvas-theme-light :global(.svelte-flow__controls button) {
		background: rgba(255, 255, 255, 0.9);
		border: 1px solid rgba(15, 23, 42, 0.12);
		color: #475569;
	}

	.flow-canvas.canvas-theme-light :global(.svelte-flow__controls button:hover) {
		background: #fff;
	}

	.flow-canvas.canvas-theme-dark :global(.svelte-flow__minimap) {
		background: rgba(18, 25, 34, 0.92);
		border: 1px solid rgba(96, 165, 250, 0.35);
	}

	.flow-canvas.canvas-theme-light :global(.svelte-flow__minimap) {
		background: rgba(255, 255, 255, 0.95);
		border: 1px solid rgba(15, 23, 42, 0.12);
	}

	.flow-canvas :global(.svelte-flow__edge-path) {
		stroke-width: 2px;
	}

	/* Edge color classes */
	.flow-canvas :global(.svelte-flow__edge.edge-default .svelte-flow__edge-path) {
		stroke: #3b82f6 !important;
	}

	.flow-canvas :global(.svelte-flow__edge.edge-true .svelte-flow__edge-path) {
		stroke: #22c55e !important;
	}

	.flow-canvas :global(.svelte-flow__edge.edge-false .svelte-flow__edge-path) {
		stroke: #ef4444 !important;
	}

	:global(.svelte-flow__node.flow-sim-node) {
		outline: 2px solid rgba(96, 165, 250, 0.95);
		outline-offset: 3px;
		border-radius: 10px;
	}

	:global(.svelte-flow__edge.flow-sim-edge .svelte-flow__edge-path) {
		stroke: #93c5fd !important;
		stroke-width: 3px !important;
	}

	.flow-canvas :global(.svelte-flow__node.selected) {
		outline: none;
	}

	/*
	 * Slightly larger ports. Do not set `position` — the library relies on `position: absolute`
	 * plus `.svelte-flow__handle-left` / `.svelte-flow__handle-right` for in vs out placement.
	 */
	:global(.svelte-flow__handle) {
		width: 18px !important;
		height: 18px !important;
		min-width: 18px !important;
		min-height: 18px !important;
		border-width: 3px !important;
		box-sizing: border-box;
		z-index: 10;
	}

	/*
	 * Anchor handle centers on the visible card edge. XYFlow's transforms (or
	 * the equivalent inline transforms for measured handles) move half of the
	 * circle outside; an additional ±radius offset would move it twice.
	 */
	:global(.svelte-flow__handle-right) {
		right: 0 !important;
	}

	:global(.svelte-flow__handle-left) {
		left: 0 !important;
	}
</style>
